import { Injectable } from "@angular/core";
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { environment } from 'src/environments/environment';
import { DataService } from './DataServices/data.service';
import { Observable, BehaviorSubject, combineLatest } from 'rxjs';
import { IResponse } from '../model/response.interface';
import { LeadClientsRepository } from './lead.clients.repository';
import { LangsRepository } from './langs.repository';
import { GetLeadClientByCrmTokenResponse } from "../model/leadClients/get.lead.client.bycrmtoken.response";
import { Client } from '../model/leadClients/lead.client.model';
import { LeadClientsList } from '../model/leadClients/lead.clients.list.interface';
import { LeadDeal } from '../model/leadClients/lead.client.deal';
import { ClientMeeting } from '../model/leadClients/lead.client.meeting';
import { Lead } from "../model/lead.model";
import { LeadClientMeetingStatus } from "../model/leadClients/lead.client.meeting.meetingstatus";

@Injectable()
export class LeadClientRepositoryBUT extends LeadClientsRepository {
    public lastReload: number = 0;
    public ready: BehaviorSubject<boolean> = new BehaviorSubject(false);
    public leadClientsReady: BehaviorSubject<boolean> = new BehaviorSubject(false);
    public leadsReady: BehaviorSubject<{ ready: boolean; leadClientId?: number }> = new BehaviorSubject({ ready: false });
    private callFiles: { [callRecordingUrl: string]: Promise<SafeUrl | null> } = {};
    private callBlobs: { [callRecordingUrl: string]: Promise<Blob> } = {};

    constructor(
        public dataService: DataService,
        protected langsRepository: LangsRepository,
        private domSanitizer: DomSanitizer
    ) {
        super(dataService, langsRepository);
        combineLatest([
            this.dataService.token,
            this.dataService.jwt,
            this.dataService.userId
        ]).subscribe(([token, jwt, userId]) => {
            this.setReadyFromAuth(token, jwt, userId);
        });
    }

    public syncReady(): void {
        this.setReadyFromAuth(
            this.dataService.token.value,
            this.dataService.jwt.value,
            this.dataService.userId.value
        );
    }

    private setReadyFromAuth(token: string, jwt: string, userId: number | null): void {
        const hasSessionUser = userId !== null && userId > 0;
        const hasJwt = jwt != null && jwt !== "";
        const isReady = !!(token || hasJwt || hasSessionUser);
        if (isReady) {
            this.ready.next(true);
        } else {
            this.leadCLients = [];
            this.ready.next(false);
        }
    }

    public getLeadClientById(id: number): Client {
        return this.leadCLients.find(x => x.id === id) || null;
    }

    public invalidate(): void {
        this.lastReload = 0;
    }

    public loadLeadClients(from: number, q: number, search: string, filter: number, sort: string, dir: number, processId: number): Observable<IResponse<LeadClientsList>> {
        this.setLeadClientsReady(false);
        return this.dataService.but_getLeadClients(from, q, search, filter, sort, dir, processId);
    }

    public loadLeadClient(id: number): Observable<IResponse<Client>> {
        return this.dataService.but_getLeadClient(id);
    }

    public sendUpdateLeadClientFollowup(id: number, followUp: string): Observable<IResponse<any>> {
        return this.dataService.but_updateLeadClientFollowUp(id, followUp);
    }

    public sendUpdateLeadClientStatus(leadClient: Client): Observable<IResponse<any>> {
        return this.dataService.but_updateLeadClientStatus(leadClient);
    }

    public sendUpdateLeadClient(id: number, leadClient: Client) {
        return this.dataService.but_updateLeadClient(leadClient);
    }

    public sendAddLeadClient(leadClient: Client): Observable<IResponse<number>> {
        return this.dataService.but_addLeadClient(leadClient);
    }

    public sendLeadClientDeal(dealId: number, leadClientId: number, subject: string, amount: number, userId: number, createdDate: string): Observable<IResponse<number>> {
        return this.dataService.but_updateLeadClientDeal(dealId, leadClientId, subject, amount, userId, createdDate);
    }

    public sendLeadClientUpdateDeal(dealId: number, leadClientId: number, subject: string, amount: number, assigneeId: number): Observable<IResponse<number>> {
        return this.dataService.but_UpdateDeal(dealId, leadClientId, subject, amount, assigneeId);
    }

    public getLeadClientDeal(id: number): Observable<IResponse<LeadDeal>> {
        return this.dataService.but_getDealById(id);
    }

    public getLeadClientMeeting(id: number): Observable<IResponse<ClientMeeting>> {
        return this.dataService.but_LeadClientGetMeeting(id);
    }

    public CancelLeadClientMeeting(id: number, locale: string): Observable<IResponse<boolean>> {
        return this.dataService.but_LeadClientCancelMeeting(id, locale);
    }

    public CancelLeadClientDeal(id: number): Observable<IResponse<boolean>> {
        return this.dataService.but_LeadClientCancelDeal(id);
    }

    public addMeeting(id: number, leadClientId: number, meetingDate: string, meetingEndDate: string, subject: string, meetingAssigneeId: number, externalId: number,
            note: string, meetingStatus: LeadClientMeetingStatus) {
        return this.dataService.but_LeadClientAddMeeting(id, leadClientId, meetingDate, meetingEndDate, subject, meetingAssigneeId, externalId, note, meetingStatus);
    }

    public updateMeeting(id: number, leadClientId: number, meetingDate: string, meetingEndDate: string, subject: string, meetingAssigneeId: number, externalId: number,
            note: string, meetingStatus: LeadClientMeetingStatus) {
        return this.dataService.but_LeadClientUpdateMeeting(id, leadClientId, meetingDate, meetingEndDate, subject, meetingAssigneeId, externalId, note, meetingStatus);
    }

    public getNotes(id: number) {
        return this.dataService.but_getLeadClientNotes(id);
    }

    public addNote(leadClientId: number, note: string, assigneeId: number, gpsLongitude: string, gpsLatitude: string, createdDate: string) {
        return this.dataService.but_addLeadClientNote(leadClientId, note, assigneeId, gpsLongitude, gpsLatitude, createdDate);
    }

    public getLeadNotes(leadId: number) {
        return this.dataService.but_getLeadNotes(leadId);
    }

    public addLeadNote(leadId: number, note: string, assigneeId: number, gpsLongitude: string, gpsLatitude: string, createdDate: string) {
        return this.dataService.but_addLeadNote(leadId, note, assigneeId, gpsLongitude, gpsLatitude, createdDate);
    }

    public getLeads(leadClientId: number): Observable<IResponse<Lead[]>> {
        return this.dataService.but_getLeadsForLeadClient(leadClientId);
    }

    public getLead(leadId: number): Observable<IResponse<Lead>> {
        return this.dataService.but_getLead(leadId);
    }

    public getDeals(leadClientId: number): Observable<IResponse<LeadDeal[]>> {
        return this.dataService.but_getDealsForLeadClient(leadClientId);
    }

    public getMeetings(leadClientId: number): Observable<IResponse<ClientMeeting[]>> {
        return this.dataService.but_getMeetingsForLeadClient(leadClientId);
    }

    public getAllWhatsappMessagesLeadClient(leadClientId: number, phone?: string) {
        return this.dataService.getAllWhatsappMessagesLeadClient(leadClientId, phone);
    }

    public getFile(url: string): Promise<Blob> {
        return this.dataService.getFile(url);
    }

    // the recording endpoint needs the bearer token, which a plain <audio src> request never sends
    public getCallFile(callRecordingUrl: string): Promise<SafeUrl | null> {
        if (!this.callFiles[callRecordingUrl]) {
            this.callFiles[callRecordingUrl] = this.getCallBlob(callRecordingUrl)
                .then(blob => this.domSanitizer.bypassSecurityTrustUrl(window.URL.createObjectURL(blob)))
                .catch(reason => {
                    delete this.callFiles[callRecordingUrl];
                    console.error(`Error when downloading call file ${callRecordingUrl} | ${JSON.stringify(reason)}`);
                    return null;
                });
        }
        return this.callFiles[callRecordingUrl];
    }

    // one download per recording for the player and the download button: the endpoint is rate-limited per IP
    public getCallBlob(callRecordingUrl: string): Promise<Blob> {
        if (!this.callBlobs[callRecordingUrl]) {
            const url = encodeURI(callRecordingUrl).replace(environment.baseUrlV1, '');
            this.callBlobs[callRecordingUrl] = this.getFile(url).catch(reason => {
                delete this.callBlobs[callRecordingUrl];
                throw reason;
            });
        }
        return this.callBlobs[callRecordingUrl];
    }

    public getMessageFile(url: string): Promise<Blob> {
        return this.dataService.getMessageFile(url);
    }

    public getTemplatesForLeadClient(leadClientId: number, phone?: string) {
        return this.dataService.getTemplatesForLeadClient(leadClientId, phone);
    }

    public markMessagesAsRead(leadClientId: number, phone?: string) {
        return this.dataService.markMessagesAsRead(leadClientId, phone);
    }

    public sendReplyWhatsappMessage(leadClientId: number, message: string, userId: number, files?: File[], whatsappPhone?: string): Observable<IResponse<any>> {
        if (files && files.length > 0) {
            return this.dataService.sendReplyWhatsappFile(leadClientId, userId, files, whatsappPhone);
        }

        return this.dataService.sendReplyWhatsappMessage(leadClientId, message, userId, whatsappPhone);
    }

    public sendTemplateWhatsappMessage(leadClientId: number, templateName: string, userId: number, whatsappPhone: string): Observable<IResponse<any>> {
        return this.dataService.sendTemplateWhatsappMessage(leadClientId, templateName, userId, whatsappPhone);
    }

    public setLeadClientsReady(value: boolean): void {
        this.leadClientsReady.next(value);
    }

    public updateLeadClientAddress(leadClientId: number, address: string, addressCoords: string): Observable<IResponse<any>> {
        return this.dataService.updateLeadClientAddress(leadClientId, address, addressCoords);
    }

    public getAdPreview(channelId: number, adGroupId: string, adId: string): Observable<IResponse<string>> {
        return this.dataService.but_getAdPreview(channelId, adGroupId, adId);
    }

    //#region WithCrmToken

    public getAllWhatsappMessagesLeadClientWithCrmToken(token: string, leadClientId: number, phone?: string) {
        return this.dataService.getAllWhatsappMessagesLeadClientWithCrmToken(token, leadClientId, phone);
    }

    public getMessageFileWithCrmToken(token: string, url: string) : Promise<Blob> {
        return this.dataService.getMessageFileWithCrmToken(token, url);
    }

    public getLeadClientWithCrmToken(token: string, id: number): Observable<IResponse<GetLeadClientByCrmTokenResponse>>{
        return this.dataService.getLeadClientWithCrmToken(token, id);
    }

    public getTemplatesForLeadClientWithCrmToken(token: string, leadClientId: number, phone?: string) {
        return this.dataService.getTemplatesForLeadClientWithCrmToken(token, leadClientId, phone);
    }

    public markMessagesAsReadWithCrmToken(token: string, leadClientId: number, phone?: string) {
        return this.dataService.markMessagesAsReadWithCrmToken(token, leadClientId, phone);
    }

    public sendReplyWhatsappMessageWithCrmToken(
        token: string,
        leadClientId: number,
        message: string,
        userId: number,
        files: File[],
        whatsappPhone?: string
    ): Observable<IResponse<any>> {
        if (files && files.length > 0) {
            return this.dataService.sendReplyWhatsappFileMessageWithCrmToken(token, leadClientId, message, userId, files, whatsappPhone);
        }

        return this.dataService.sendReplyWhatsappMessageWithCrmToken(token, leadClientId, message, userId, whatsappPhone);
    }

    public sendTemplateWhatsappMessagetWithCrmToken(
        token: string,
        leadClientId: number,
        templateName: string,
        userId: number,
        whatsappPhone: string
    ): Observable<IResponse<any>> {
        return this.dataService.sendTemplateWhatsappMessageWithCrmToken(token, leadClientId, templateName, userId, whatsappPhone);
    }

    public getAdPreviewWithCrmToken(token: string, channelId: number, adGroupId: string, adId: string): Observable<IResponse<string>> {
        return this.dataService.but_getAdPreviewWithCrmToken(token, channelId, adGroupId, adId);
    }

    //#endregion WithCrmToken
}